/**
 * A contact's ladder on their CONTACTS page (WP-X1, docs/CONTACTS.md, docs/UI.md "The little black book"): where
 * you stand with them, the five rungs with what each opens, the next rung and exactly how to reach it.
 *
 * `ladderFor` is pure (unit-tested in book.test.ts); `renderLadder` draws it on the right page above the
 * contact's own words (bookScreen.ts calls it for an entry with a `ladder`).
 */
import {
  CONTACT_DAILY_CAP,
  MAX_RUNG,
  TRACKS,
  earnText,
  progressOf,
  rungPerks,
  type ContactMode,
  type ContactTrack,
  type Standing,
} from '../../shared/contacts';

export interface BookLadderRung {
  n: number;
  name: string;
  title: boolean;
  /** What it opens, in plain words. */
  perks: string[];
  state: 'done' | 'next' | 'later';
}

export interface BookLadder {
  track: ContactTrack;
  /** "Trusted" (rung 2 of 5), or "Not known yet". */
  standing: string;
  rung: number;
  points: number;
  /** Points for the next rung (null at the top). */
  next: number | null;
  /** 0..1 towards the next rung. */
  frac: number;
  /** One plain line: what the next rung is and how far off. */
  nextLine: string;
  /** How points are earned with this contact (one line per kind of work). */
  earn: string[];
  /** The daily allowance for repeat work, and what's left of it today (offline; null online). */
  today: string | null;
  /** Rungs top (5) to bottom (1). */
  rungs: BookLadderRung[];
}

/** The rung name for a standing ("Trusted"), or "Not known yet". */
export function standingName(track: ContactTrack, rung: number): string {
  return rung > 0 ? TRACKS[track].rungs[rung - 1]!.name : 'Not known yet';
}

export function ladderFor(track: ContactTrack, st: Standing, mode: ContactMode, todayUsed: number | null = null): BookLadder {
  const def = TRACKS[track];
  const p = progressOf(st[track] ?? 0);
  const rungs: BookLadderRung[] = [];
  for (let n = MAX_RUNG; n >= 1; n--) {
    const r = def.rungs[n - 1]!;
    const perks = rungPerks(track, n, mode).map((x) => x.text);
    if (r.title) perks.push(`A title: “${r.name}”`);
    rungs.push({ n, name: r.name, title: !!r.title, perks, state: n <= p.rung ? 'done' : n === p.rung + 1 ? 'next' : 'later' });
  }
  const nextName = p.rung < MAX_RUNG ? def.rungs[p.rung]!.name : null;
  const need = p.next === null ? 0 : p.next - p.points;
  const nextLine =
    nextName === null
      ? `Top of the ladder with ${def.short}. Nothing more to earn here — it's all yours.`
      : `Next: “${nextName}” — ${need} more point${need === 1 ? '' : 's'} (${p.points} of ${p.next}).`;
  const left = todayUsed === null ? null : Math.max(0, CONTACT_DAILY_CAP - todayUsed);
  return {
    track,
    standing: p.rung > 0 ? `${standingName(track, p.rung)} (rung ${p.rung} of ${MAX_RUNG})` : 'Not known yet',
    rung: p.rung,
    points: p.points,
    next: p.next,
    frac: p.frac,
    nextLine,
    earn: earnText(track, mode),
    today:
      left === null
        ? `Repeat work counts for up to ${CONTACT_DAILY_CAP} points a day.`
        : left > 0
          ? `Repeat work counts for up to ${CONTACT_DAILY_CAP} points a day: ${left} left today.`
          : `That's ${CONTACT_DAILY_CAP} points from repeat work today — the rest counts again tomorrow.`,
    rungs,
  };
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text: string | null, parent: HTMLElement): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== null) e.textContent = text;
  parent.appendChild(e);
  return e;
};

/** Draw the ladder (right page). */
export function renderLadder(parent: HTMLElement, l: BookLadder): void {
  const box = el('div', 'bk-ladder', null, parent);
  box.dataset.track = l.track;
  box.dataset.rung = String(l.rung);
  const head = el('div', 'bk-ladder-head', null, box);
  el('span', 'bk-ladder-label', 'Standing', head);
  el('b', 'bk-ladder-now', l.standing, head);
  const bar = el('div', 'bk-ladder-bar', null, box);
  const fill = el('i', '', null, bar);
  fill.style.width = `${Math.round(l.frac * 100)}%`;
  el('p', 'bk-ladder-next', l.nextLine, box);
  const list = el('ol', 'bk-ladder-rungs', null, box);
  for (const r of l.rungs) {
    const li = el('li', `r-${r.state}`, null, list);
    li.dataset.n = String(r.n);
    const top = el('div', 'bk-rung-top', null, li);
    el('span', 'bk-rung-n', String(r.n), top);
    el('span', 'bk-rung-name', r.name, top);
    if (r.state === 'done') el('span', 'bk-rung-tag done', '✓', top);
    else if (r.state === 'next') el('span', 'bk-rung-tag next', 'Next', top);
    if (r.perks.length) el('small', 'bk-rung-perks', r.perks.join(' · '), li);
  }
  const how = el('div', 'bk-ladder-how', null, box);
  el('span', 'bk-ladder-label', 'How to climb', how);
  const ul = el('ul', '', null, how);
  for (const e of l.earn) el('li', '', e, ul);
  if (l.today) el('small', 'bk-ladder-today', l.today, how);
}
